'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { matchesSearch } from '../../utils/matchesSearch';
import { Text } from '../typography/Text';
import { Button } from './Button';
import { Command, CommandEmpty, CommandInput, CommandItem } from './Command';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { selectTriggerVariants } from './Select';
type VirtualizedSingleSelectOption = {
  value: string;
  component: ReactNode;
  keywords?: string[];
  /**
   * Heading rendered above this option whenever it differs from the previous
   * option's group. Options are not reordered — the consumer sorts grouped
   * options together.
   */
  group?: string;
};
type VirtualRow = {
  kind: 'heading';
  key: string;
  label: string;
} | {
  kind: 'option';
  key: string;
  option: VirtualizedSingleSelectOption;
};
function toVirtualRows(options: VirtualizedSingleSelectOption[]): VirtualRow[] {
  const rows: VirtualRow[] = [];
  let previousGroup: string | undefined;
  for (const option of options) {
    if (option.group !== undefined && option.group !== previousGroup) {
      rows.push({
        kind: 'heading',
        key: `heading:${option.group}`,
        label: option.group
      });
    }
    previousGroup = option.group;
    rows.push({
      kind: 'option',
      key: option.value,
      option
    });
  }
  return rows;
}
type VirtualizedSingleSelectProps = Pick<ComponentProps<typeof PopoverContent>, 'align' | 'side' | 'sideOffset'> & {
  options: VirtualizedSingleSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  variant?: 'default' | 'secondary';
  size?: 'default' | 'sm';
  triggerClassName?: string;
  searchPlaceholder?: string;
  estimateItemSize?: number;
  clearable?: boolean;
  disabled?: boolean;
  triggerIcon?: ReactNode;
  onSearchChange?: (search: string) => void;
  emptyState?: ReactNode;
  'data-testid'?: string;
};
export function VirtualizedSingleSelect(props: VirtualizedSingleSelectProps) {
  const {
    options,
    value,
    onChange,
    placeholder = 'Select…',
    variant,
    size,
    triggerClassName,
    align = 'start',
    side,
    sideOffset,
    searchPlaceholder = 'Search…',
    estimateItemSize = 32,
    clearable = true,
    disabled = false,
    triggerIcon,
    onSearchChange,
    emptyState,
    'data-testid': testId
  } = props;
  const [open, setOpen] = useState(false);
  const [search, _setSearch] = useState('');
  const [activeValue, setActiveValue] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = useMemo(() => {
    if (value == null) return null;
    return options.find(o => o.value === value) ?? null;
  }, [options, value]);
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter(o_0 => matchesSearch(o_0, search));
  }, [options, search]);
  const rows = useMemo(() => toVirtualRows(filteredOptions), [filteredOptions]);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateItemSize,
    overscan: 5
  });
  const parentRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    if (node) virtualizer.measure();
  }, [virtualizer]);

  // Scroll the virtualizer to keep the keyboard-active item visible
  useEffect(() => {
    if (!activeValue) return;
    const index = rows.findIndex(row => row.kind === 'option' && row.option.value.toLowerCase() === activeValue.toLowerCase());
    if (index !== -1) virtualizer.scrollToIndex(index);
  }, [activeValue, rows, virtualizer]);
  function updateSearch(value_0: string) {
    _setSearch(value_0);
    onSearchChange?.(value_0);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }
  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) updateSearch('');
  }
  function handleSelect(optionValue: string) {
    onChange(optionValue);
    handleOpenChange(false);
  }
  function handleClear() {
    onChange(null);
    handleOpenChange(false);
  }
  function handleInputKeyDown(e: React.KeyboardEvent) {
    const count = filteredOptions.length;
    if (count === 0) return;
    const currentIndex = filteredOptions.findIndex(o_1 => o_1.value.toLowerCase() === activeValue.toLowerCase());
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      const nextIndex = currentIndex < count - 1 ? currentIndex + 1 : 0;
      const next = filteredOptions[nextIndex];
      if (next) setActiveValue(next.value);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : count - 1;
      const prev = filteredOptions[prevIndex];
      if (prev) setActiveValue(prev.value);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const current = filteredOptions[currentIndex];
      if (current) handleSelect(current.value);
    }
  }
  return <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} data-placeholder={selectedOption ? undefined : ''} data-testid={testId} disabled={disabled} className={cn(selectTriggerVariants({
        variant,
        size
      }), triggerClassName)}>
          {selectedOption ? <span className="flex items-center gap-1 truncate">{selectedOption.component}</span> : <Text tag="span" size="2xs" className="truncate">
              {placeholder}
            </Text>}
          {triggerIcon ?? <ChevronDownIcon className="ml-auto size-4 shrink-0 transition-transform group-data-[state=open]:-rotate-180" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align={align} side={side} sideOffset={sideOffset}>
        <Command shouldFilter={false} value={activeValue} onValueChange={setActiveValue}>
          <div className="flex h-9 items-center border-b *:data-[slot=command-input-wrapper]:flex-1">
            <CommandInput placeholder={searchPlaceholder} className="text-xs" value={search} onValueChange={updateSearch} onKeyDown={handleInputKeyDown} data-testid={testId ? `${testId}-search` : undefined} />
            {clearable && value != null && <Button type="button" onClick={handleClear} onKeyDown={e_0 => {
            if (e_0.key === 'Enter') {
              e_0.preventDefault();
              e_0.stopPropagation();
              handleClear();
            }
          }} variant="ghost" size="sm" className="mr-2 ml-auto shrink-0">
                Clear
              </Button>}
          </div>
          {filteredOptions.length === 0 ? <CommandEmpty>{emptyState ?? 'No results found.'}</CommandEmpty> : <div ref={parentRef} className="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto p-1">
              <div className="relative w-full" style={{
            height: virtualizer.getTotalSize()
          }}>
                {virtualizer.getVirtualItems().map(virtualRow => {
              const row_0 = rows[virtualRow.index];
              if (!row_0) return null;
              if (row_0.kind === 'heading') {
                return <div key={row_0.key} ref={virtualizer.measureElement} data-index={virtualRow.index} className="absolute top-0 left-0 w-full px-2 pt-2 pb-1" style={{
                  transform: `translateY(${virtualRow.start}px)`
                }}>
                        <Text size="4xs" tag="span" className="text-secondary-foreground">
                          {row_0.label}
                        </Text>
                      </div>;
              }
              const {
                option
              } = row_0;
              return <CommandItem key={option.value} ref={virtualizer.measureElement} data-index={virtualRow.index} value={option.value} onSelect={() => handleSelect(option.value)} className="absolute top-0 left-0 w-full" style={{
                transform: `translateY(${virtualRow.start}px)`
              }}>
                      <span className="flex-1 overflow-hidden">{option.component}</span>
                      <CheckIcon className={cn('size-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')} />
                    </CommandItem>;
            })}
              </div>
            </div>}
        </Command>
      </PopoverContent>
    </Popover>;
}
export type { VirtualizedSingleSelectOption, VirtualizedSingleSelectProps };